import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  Alert,
  CircularProgress,
  TextField,
} from '@mui/material';
import axios from 'axios';

type Status = 'verifying' | 'success' | 'error' | 'missing';

export const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<Status>('verifying');
  const [message, setMessage] = useState('');

  // Resend state
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

  const verify = useCallback(async () => {
    if (!token) {
      setStatus('missing');
      return;
    }

    try {
      setStatus('verifying');
      await axios.get(`${apiUrl}/register/verify-email`, { params: { token } });
      setStatus('success');
      setMessage('Your email has been verified. Your account is now pending admin approval.');
      // Redirect to login after a short delay
      setTimeout(() => navigate('/login'), 4000);
    } catch (err: unknown) {
      setStatus('error');
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setMessage(
          axiosError.response?.data?.message ||
            'We could not verify your email. The link may be invalid or expired.'
        );
      } else {
        setMessage('We could not verify your email. Please try again later.');
      }
    }
  }, [token, apiUrl, navigate]);

  useEffect(() => {
    verify();
  }, [verify]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    setResendMessage('');
    setResending(true);

    try {
      await axios.post(`${apiUrl}/register/resend-verification`, { email: resendEmail });
      setResendMessage(
        'If an unverified account exists for that email, a new verification link has been sent.'
      );
    } catch {
      setResendMessage(
        'If an unverified account exists for that email, a new verification link has been sent.'
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #2a2a2a 100%)',
        py: 4,
      }}
    >
      <Container component="main" maxWidth="xs">
        <Paper elevation={8} sx={{ padding: 4, width: '100%', textAlign: 'center' }}>
          <Box
            component="img"
            src="/logo.bmp"
            alt="GKM Tennis Academy"
            sx={{ width: 90, height: 'auto', mb: 3, mx: 'auto', display: 'block', borderRadius: '8px' }}
          />

          {status === 'verifying' && (
            <>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6">Verifying your email...</Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <Typography variant="h5" gutterBottom color="success.main" fontWeight={700}>
                ✓ Email Verified
              </Typography>
              <Alert severity="success" sx={{ mt: 2, textAlign: 'left' }}>
                {message}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                Redirecting to login...
              </Typography>
              <Button component={Link} to="/login" variant="contained" sx={{ mt: 2 }} fullWidth>
                Go to Login
              </Button>
            </>
          )}

          {status === 'missing' && (
            <>
              <Typography variant="h6" gutterBottom fontWeight={700}>
                Missing verification token
              </Typography>
              <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
                This link is missing its verification token. Please use the link from your
                verification email, or request a new one below.
              </Alert>
            </>
          )}

          {status === 'error' && (
            <>
              <Typography variant="h6" gutterBottom color="error.main" fontWeight={700}>
                Verification failed
              </Typography>
              <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
                {message}
              </Alert>
            </>
          )}

          {/* Resend form — shown for error/missing states */}
          {(status === 'error' || status === 'missing') && (
            <Box component="form" onSubmit={handleResend} sx={{ mt: 3, textAlign: 'left' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Resend verification email
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="email"
                label="Email address"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                disabled={resending}
                required
                sx={{ mb: 1.5 }}
              />
              <Button type="submit" variant="contained" fullWidth disabled={resending}>
                {resending ? <CircularProgress size={22} /> : 'Resend Email'}
              </Button>
              {resendMessage && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {resendMessage}
                </Alert>
              )}
            </Box>
          )}

          <Box sx={{ mt: 3 }}>
            <Link to="/login" style={{ textDecoration: 'none', color: '#2563eb' }}>
              Back to Sign In
            </Link>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
